<script setup lang="ts">
import ConfirmActionDialog from "@/components/dialogs/ConfirmActionDialog.vue";
import DataTableFilterBanner from "@/components/shared/DataTableFilterBanner.vue";
import EntityActionButtons from "@/components/shared/EntityActionButtons.vue";
import EntityListTable from "@/components/shared/EntityListTable.vue";
import {
  EMPTY_STOCK_RECONCILIATION_STATS,
  GetStockReconciliationStats,
  GetStockReconciliations,
  SubmitStockReconciliation,
  type StockReconciliationStats,
} from "@/api/stockReconciliationApi";
import type { FrappeFilterDefinition } from "@/composables/useFrappeListFilters";
import { canDoctype } from "@/helper/permissionHelper";
import { useReportEngine } from "@/modules/reports/composables/useReportEngine";
import type { FrappeListFilterCondition } from "@/services/frappe/filters";
import { presentStockReconciliationError } from "@/services/stockReconciliationErrors";
import useToastify from "@/stores/toastify.store";
import type { StockReconciliationListItem } from "@/type/stockReconciliation";

/**
 * ⚠️ The permission key is an INLINE LITERAL and must stay one. `definePage` meta
 * is hoisted out of component scope at compile time, so a `PAGE_KEYS.…` reference
 * here throws "PAGE_KEYS is not defined" at runtime.
 */
definePage({ meta: { permissionKey: "page.warehouse.stock_reconciliations" } });

const STOCK_RECONCILIATION_DOCTYPE = "Stock Reconciliation";

const { t, locale } = useI18n();
const router = useRouter();
const toast = useToastify();

const submitDialogVisible = ref(false);
const submitting = ref(false);
const selectedCount = ref<StockReconciliationListItem | null>(null);
const stats = ref<StockReconciliationStats>({ ...EMPTY_STOCK_RECONCILIATION_STATS });

const canCreate = computed(() => canDoctype(STOCK_RECONCILIATION_DOCTYPE, "create"));

/**
 * ⚠️ Submitting posts the stock ledger and cannot be undone, so it is gated on the
 * doctype's own `submit` permission rather than on mere page access. Page
 * visibility never authorizes an operation.
 */
const canSubmit = computed(() => canDoctype(STOCK_RECONCILIATION_DOCTYPE, "submit"));

interface StockReconciliationFilterState {
  search: string;
  purpose: string | null;
  status: string | null;
  fromDate: string | null;
  toDate: string | null;
  conditions: FrappeListFilterCondition[];
}

/**
 * ⚠️ Returns `undefined` for anything unrecognised, and that is the whole point.
 *
 * The obvious ternary sends every unknown value — an empty select included — to
 * `0`, so clearing the status filter would quietly narrow the list to drafts while
 * looking unfiltered. `useFrappeListFilters` drops a binding whose value is
 * `undefined`, so the clause simply never exists.
 */
function statusToDocstatus(value: unknown) {
  if (value === "Submitted")
    return 1;
  if (value === "Cancelled")
    return 2;
  if (value === "Draft")
    return 0;

  return undefined;
}

/**
 * ⚠️ Seven headers, ordered so the four that matter come first.
 *
 * On a phone `EntityListTable` swaps itself for a card list that shows only the
 * first four fields before a "show more" expander — so this order decides what an
 * operator sees without tapping.
 */
const headers = computed(() => [
  { title: t("alkokh.stockReconciliation.columns.reconciliation"), key: "reconciliation" },
  { title: t("alkokh.stockReconciliation.columns.date"), key: "postingDate" },
  { title: t("alkokh.global.status"), key: "status" },
  { title: t("alkokh.stockReconciliation.columns.difference"), key: "differenceAmount" },
  { title: t("alkokh.stockReconciliation.columns.purpose"), key: "purpose" },
  { title: t("alkokh.stockReconciliation.columns.company"), key: "company" },
  { title: t("alkokh.global.actions"), key: "actions", sortable: false },
]);

const purposeOptions = computed(() => [
  { label: t("alkokh.stockReconciliation.purposes.reconciliation"), value: "Stock Reconciliation" },
  { label: t("alkokh.stockReconciliation.purposes.openingStock"), value: "Opening Stock" },
]);

const statusOptions = computed(() => [
  { label: t("alkokh.stockReconciliation.status.draft"), value: "Draft" },
  { label: t("alkokh.stockReconciliation.status.submitted"), value: "Submitted" },
  { label: t("alkokh.stockReconciliation.status.cancelled"), value: "Cancelled" },
]);

const filterDefinitions = computed<FrappeFilterDefinition<StockReconciliationFilterState>[]>(() => [
  {
    // Hidden because the banner renders the condition builder itself.
    key: "conditions",
    type: "conditions",
    hidden: true,
  },
  {
    key: "purpose",
    type: "select",
    label: t("alkokh.stockReconciliation.filters.purpose"),
    placeholder: t("alkokh.stockReconciliation.filters.allPurposes"),
    icon: "tabler-scale",
    options: purposeOptions.value,
    doctype: STOCK_RECONCILIATION_DOCTYPE,
    field: "purpose",
    operator: "=",
    enabled: value => Boolean(value),
  },
  {
    key: "status",
    type: "select",
    label: t("alkokh.stockReconciliation.filters.status"),
    placeholder: t("alkokh.stockReconciliation.filters.allStatuses"),
    icon: "tabler-file-check",
    options: statusOptions.value,
    doctype: STOCK_RECONCILIATION_DOCTYPE,
    field: "docstatus",
    operator: "=",
    transform: statusToDocstatus,
    enabled: value => Boolean(value),
  },
  {
    key: "fromDate",
    type: "date",
    label: t("alkokh.stockReconciliation.filters.fromDate"),
    icon: "tabler-calendar-event",
    doctype: STOCK_RECONCILIATION_DOCTYPE,
    field: "posting_date",
    operator: ">=",
    enabled: value => Boolean(value),
  },
  {
    key: "toDate",
    type: "date",
    label: t("alkokh.stockReconciliation.filters.toDate"),
    icon: "tabler-calendar-event",
    doctype: STOCK_RECONCILIATION_DOCTYPE,
    field: "posting_date",
    operator: "<=",
    enabled: value => Boolean(value),
  },
]);

/**
 * What the condition builder may filter on: only fields this list can actually
 * SHOW, plus the standard audit ones. Filtering on something invisible leaves the
 * operator with a shortened list and no way to see why those rows survived.
 *
 * ⚠️ The standard fields are whitelisted EXPLICITLY — the builder gates them too.
 */
const conditionAllowedFields = [
  "name",
  "purpose",
  "company",
  "posting_date",
  "posting_time",
  "expense_account",
  "cost_center",
  "difference_amount",
  "docstatus",
  "owner",
  "creation",
  "modified",
];

const conditionFieldLabelOverrides = computed(() => ({
  name: t("alkokh.stockReconciliation.columns.reconciliation"),
  purpose: t("alkokh.stockReconciliation.filters.purpose"),
  company: t("alkokh.stockReconciliation.columns.company"),
  posting_date: t("alkokh.stockReconciliation.form.postingDate"),
  posting_time: t("alkokh.stockReconciliation.form.postingTime"),
  expense_account: t("alkokh.stockReconciliation.form.expenseAccount"),
  cost_center: t("alkokh.stockReconciliation.form.costCenter"),
  difference_amount: t("alkokh.stockReconciliation.columns.difference"),
  docstatus: t("alkokh.global.status"),
}));

/**
 * Column key -> the field the backend can actually order by.
 *
 * ⚠️ Not a tidy-up. Interpolating a Vuetify column key straight into `order_by`
 * produces an invalid sort for every column whose key is not also a real database
 * column; the request fails and the list renders empty — so sorting looks like
 * "there are no stock counts".
 */
const SORT_FIELD_BY_COLUMN: Record<string, string> = {
  reconciliation: "name",
  purpose: "purpose",
  postingDate: "posting_date",
  company: "company",
  status: "docstatus",
  differenceAmount: "difference_amount",
};

function resolveSortField(key?: string) {
  return (key && SORT_FIELD_BY_COLUMN[key]) || "modified";
}

const numberFormatter = computed(() =>
  new Intl.NumberFormat(locale.value === "ar" ? "ar-IQ-u-nu-latn" : "en-US", {
    maximumFractionDigits: 2,
  }),
);

/** `null` is UNKNOWN — the backend has not valued this draft — and must not print as `0`. */
function formatNumber(value: number | null | undefined) {
  return value == null ? "—" : numberFormatter.value.format(Number(value));
}

function resolvePurposeLabel(value: string) {
  return purposeOptions.value.find(option => option.value === value)?.label || value || "—";
}

function resolveStatusLabel(value: string) {
  return statusOptions.value.find(option => option.value === value)?.label || value || "—";
}

function resolveStatusColor(status: string) {
  if (status === "Submitted")
    return "success";
  if (status === "Cancelled")
    return "error";

  return "warning";
}

const {
  items,
  total,
  loading,
  searchQuery,
  filterValues,
  pagination,
  tableSortBy,
  fetchItems,
  updateOptions,
  resetState,
} = useReportEngine<StockReconciliationListItem, StockReconciliationFilterState>({
  fetcher: async ({ search, filters, orFilters, page, itemsPerPage, sortBy, orderBy }) => {
    // One query object for both calls, so the tiles can never count a different
    // set than the table lists.
    const query = { search, filters, orFilters };

    const [result, statsResult] = await Promise.all([
      GetStockReconciliations(ref(false), {
        ...query,
        limit_start: (page - 1) * itemsPerPage,
        limit_page_length: itemsPerPage,
        order_by: `${resolveSortField(sortBy)} ${orderBy || "desc"}`,
      }),
      GetStockReconciliationStats(ref(false), query),
    ]);

    stats.value = statsResult ?? { ...EMPTY_STOCK_RECONCILIATION_STATS };

    return result;
  },
  filterDefinitions,
  initialFilterValues: {
    purpose: null,
    status: null,
    fromDate: null,
    toDate: null,
    conditions: [],
  },
  initialPageSize: 10,
  pageSizeOptions: [10, 20, 50, 100],
  // A count register is read newest-count-first. `modified desc` would reorder an
  // old count to the top the moment someone opened and re-saved it.
  initialSortBy: { key: "postingDate", order: "desc" },
  persist: { key: "warehouse-stock-reconciliations", allowedConditionFields: conditionAllowedFields },
});

const hasActiveFilters = computed(() =>
  Boolean(
    filterValues.value?.purpose
    || filterValues.value?.status
    || filterValues.value?.fromDate
    || filterValues.value?.toDate
    || filterValues.value?.conditions?.length,
  ),
);

/**
 * ⚠️ Two of these count the WHOLE filtered set and one only the page on screen,
 * and the labels say which is which. There is no aggregate endpoint to sum the
 * value change properly, and an unqualified "Value change" under a filtered list
 * reads unmistakably as a period total — a number an operator would quote.
 */
const summaryCards = computed(() => [
  {
    label: t("alkokh.stockReconciliation.metrics.total"),
    value: String(total.value),
    color: "secondary",
  },
  {
    label: t("alkokh.stockReconciliation.metrics.submitted"),
    value: String(stats.value.submitted),
    color: "success",
  },
  {
    label: t("alkokh.stockReconciliation.metrics.draft"),
    value: String(stats.value.draft),
    color: "warning",
  },
  {
    label: t("alkokh.stockReconciliation.metrics.netDifference"),
    value: formatNumber(
      items.value.reduce((sum, row) => sum + (row.differenceAmount ?? 0), 0),
    ),
    color: "primary",
  },
]);

function updateFilters(value: Record<string, unknown>) {
  filterValues.value = value as Omit<StockReconciliationFilterState, "search">;
}

function openView(item: StockReconciliationListItem) {
  router.push(`/warehouse/stock-reconciliations/${item.id}`);
}

function openEdit(item: StockReconciliationListItem) {
  router.push({
    path: `/warehouse/stock-reconciliations/${item.id}`,
    query: { mode: "edit" },
  });
}

function askSubmit(item: StockReconciliationListItem) {
  selectedCount.value = item;
  submitDialogVisible.value = true;
}

async function submitSelected() {
  if (!selectedCount.value)
    return;

  try {
    const submitted = await SubmitStockReconciliation(submitting, selectedCount.value.id);
    if (!submitted)
      return;

    toast.success({ text: t("alkokh.stockReconciliation.messages.submitted") });
    submitDialogVisible.value = false;
    selectedCount.value = null;
    await fetchItems();
  } catch (error: any) {
    const presentation = presentStockReconciliationError(error, t);
    toast.error({ text: presentation.toastMessage });
  }
}
</script>

<template>
  <div class="reco-page d-flex flex-column gap-6">
    <VRow>
      <VCol
        v-for="card in summaryCards"
        :key="card.label"
        cols="12"
        sm="6"
        xl="3"
      >
        <VCard
          variant="outlined"
          class="reco-page__metric-card"
        >
          <VCardText>
            <div class="text-body-2 text-medium-emphasis mb-2">
              {{ card.label }}
            </div>
            <div
              class="text-h4 font-weight-semibold"
              :class="`text-${card.color}`"
            >
              {{ card.value }}
            </div>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <div class="d-flex align-center justify-space-between flex-wrap gap-3">
      <div>
        <h1 class="text-h4 font-weight-semibold">
          {{ t("alkokh.stockReconciliation.title") }}
        </h1>
        <div class="text-body-2 text-medium-emphasis mt-1">
          {{ t("alkokh.stockReconciliation.subtitle") }}
        </div>
      </div>

      <VBtn
        v-if="canCreate"
        color="primary"
        prepend-icon="tabler-plus"
        to="/warehouse/stock-reconciliations/create"
      >
        {{ t("alkokh.stockReconciliation.createAction") }}
      </VBtn>
    </div>

    <DataTableFilterBanner
      :search="searchQuery"
      :model-value="filterValues"
      :filter-definitions="filterDefinitions"
      :doctype="STOCK_RECONCILIATION_DOCTYPE"
      :condition-allowed-fields="conditionAllowedFields"
      :condition-field-label-overrides="conditionFieldLabelOverrides"
      :search-placeholder="t('alkokh.stockReconciliation.searchPlaceholder')"
      :loading="loading"
      @update:search="searchQuery = $event"
      @update:model-value="updateFilters"
      @reset="resetState"
      @refresh="fetchItems"
    />

    <VCard
      variant="outlined"
      class="reco-page__table-card"
    >
      <EntityListTable
        :headers="headers"
        :items="items"
        :items-length="total"
        :loading="loading"
        :pagination="pagination"
        :fetch-items="fetchItems"
        :sort-by="tableSortBy"
        item-value="id"
        :filtered="Boolean(searchQuery) || hasActiveFilters"
        :no-data-text="t('alkokh.stockReconciliation.empty.noRecords')"
        :empty-action-label="canCreate ? t('alkokh.stockReconciliation.empty.createFirst') : ''"
        @update:options="updateOptions"
        @empty-action="router.push('/warehouse/stock-reconciliations/create')"
        @clear-filters="resetState"
      >
        <template #item.reconciliation="{ item }">
          <div class="d-flex flex-column py-2">
            <span class="font-weight-medium">{{ item.id }}</span>
          </div>
        </template>

        <template #item.purpose="{ item }">
          <span>{{ resolvePurposeLabel(item.purpose) }}</span>
        </template>

        <template #item.postingDate="{ item }">
          <div class="d-flex flex-column py-2">
            <span>{{ item.postingDate || "—" }}</span>
            <span
              v-if="item.postingTime"
              class="text-body-2 text-medium-emphasis"
            >
              {{ item.postingTime }}
            </span>
          </div>
        </template>

        <template #item.company="{ item }">
          <span>{{ item.company || "—" }}</span>
        </template>

        <template #item.status="{ item }">
          <VChip
            size="small"
            variant="outlined"
            class="reco-page__chip"
            :color="resolveStatusColor(item.status)"
          >
            {{ resolveStatusLabel(item.status) }}
          </VChip>
        </template>

        <template #item.differenceAmount="{ item }">
          <span>{{ formatNumber(item.differenceAmount) }}</span>
        </template>

        <template #item.actions="{ item }">
          <div class="d-flex align-center gap-1 py-2">
            <EntityActionButtons
              :show-view="true"
              :show-edit="item.status === 'Draft'"
              :show-delete="false"
              @view="openView(item)"
              @edit="openEdit(item)"
            />

            <VBtn
              v-if="item.status === 'Draft' && canSubmit"
              icon="tabler-check"
              variant="text"
              size="small"
              color="success"
              :loading="submitting && selectedCount?.id === item.id"
              @click="askSubmit(item)"
            />
          </div>
        </template>
      </EntityListTable>
    </VCard>

    <ConfirmActionDialog
      v-model:is-dialog-visible="submitDialogVisible"
      :title="t('alkokh.stockReconciliation.submitDialog.title')"
      :message="t('alkokh.stockReconciliation.submitDialog.message', { id: selectedCount?.id || '—' })"
      :confirm-btn="t('alkokh.stockReconciliation.submitDialog.confirm')"
      confirm-color="success"
      icon="tabler-check"
      :loading="submitting"
      @confirm="submitSelected"
    />
  </div>
</template>

<style scoped>
.reco-page {
  background: transparent;
}

.reco-page__metric-card,
.reco-page__table-card,
.reco-page__chip {
  background: rgb(var(--v-theme-surface));
}
</style>
